import { ChevronRight, Image, Search } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { t } from '../i18n';
import { $showNsfw } from '../store/app.store';
import type { AnimeSubject, SubjectPage } from '../types/bgm';
import { safeUrl } from '../utils/url';
import { useApp } from './app-context';

export const SearchBox = () => {
  const { bgm, navigate } = useApp();
  const [keyword, setKeyword] = useState(() => new URLSearchParams(location.search).get('q') ?? '');
  const [subjects, setSubjects] = useState<AnimeSubject[]>([]);
  const [open, setOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const query = keyword.trim();
    if (query.length < 2) {
      setSubjects([]);
      setOpen(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await bgm<SubjectPage>('/v0/search/subjects?limit=10&offset=0', {
          method: 'POST',
          signal: controller.signal,
          body: JSON.stringify({
            keyword: query,
            sort: 'match',
            filter: { type: [2], tag: ['日本'], ...(!$showNsfw.get() ? { nsfw: false } : {}) },
          }),
        });
        setSubjects(result.data);
        setOpen(Boolean(result.data.length));
      } catch {
        if (!controller.signal.aborted) {
          setSubjects([]);
          setOpen(false);
        }
      }
    }, 260);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [bgm, keyword]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!formRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const submit = (): void => {
    const query = keyword.trim();
    if (query) {
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  return (
    <form
      className="header-search"
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Search />
      <input
        name="keyword"
        value={keyword}
        placeholder={t('searchPlaceholder')}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        onFocus={() => setOpen(Boolean(subjects.length))}
        onChange={(event) => setKeyword(event.target.value)}
      />
      <div className={`search-suggestions${open ? ' open' : ''}`} role="listbox" aria-hidden={!open}>
        {subjects.map((subject, index) => {
          const image = safeUrl(subject.images?.small ?? subject.images?.medium);
          const score = subject.score ?? subject.rating?.score;
          return (
            <a
              className="search-suggestion"
              href={`/subjects/${subject.id}`}
              role="option"
              style={{ '--suggestion-index': index } as CSSProperties}
              onClick={(event) => {
                event.preventDefault();
                setOpen(false);
                navigate(`/subjects/${subject.id}`);
              }}
              key={subject.id}
            >
              <span className="search-suggestion-cover">
                {image ? <img src={image} alt="" referrerPolicy="no-referrer" /> : <Image />}
              </span>
              <span className="search-suggestion-copy">
                <strong>{subject.name_cn || subject.name}</strong>
                {subject.name_cn ? <small>{subject.name}</small> : null}
                <small>
                  {[subject.date?.slice(0, 4), score ? `★ ${score.toFixed(1)}` : ''].filter(Boolean).join(' · ')}
                </small>
              </span>
              <ChevronRight className="search-suggestion-arrow" />
            </a>
          );
        })}
      </div>
    </form>
  );
};
